/*
  Kiwi Ears · measurement lab (Rene 2026-08-31) — sections/ms-lab.

  The PDP frequency-response visualizer (assets/pk-tuning.js: D3 v7, purple
  radial field, log x, named band strip) generalized from "main + one
  compare" to UP TO FOUR PICKED CURVES plus an optional baseline. Picking a
  tile fetches /products/<handle>?view=frdata (the same endpoint the PDP
  compare picker uses) and draws the curve in the slot's color; nothing else
  is built in JS — tiles, chips container, toggle and card are all
  server-rendered by the section.

  Normalize (default ON) shifts every curve so it reads 60 dB at 1 kHz —
  rigs and sensitivities differ, and unnormalized absolute levels turn a
  tonality comparison into a loudness comparison. The raw view stays one
  toggle away. Chosen models ride the URL (?iems=handle,handle).
*/
(function () {
  'use strict';
  var root = document.querySelector('[data-ms]');
  var mount = document.getElementById('ms-fr-chart');
  if (!root || !mount || !window.d3) return;
  var d3 = window.d3;

  var MAXN = parseInt(root.getAttribute('data-ms-max'), 10) || 4;
  var PALETTE = ['#00FF99', '#FFF200', '#FF6C9C', '#9B83FF', '#7FD8FF'];
  var FIELD_STOPS = [[0, '#5A4DCB'], [0.25, '#4A40A5'], [0.5, '#393280'], [0.75, '#29255A'], [1, '#181734']];
  var AREA_TOP = '#00E58A', AREA_BOTTOM = '#11FFBF';
  var FONT_SANS = "'Robout',sans-serif";
  var FONT_MONO = "'JetBrains Mono',ui-monospace,monospace";
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- keep every curve inside the plotted span -------------------------
     The x axis is a LOG domain of 20 Hz – 20 kHz and d3 log scales do not
     clamp, so any sample outside that span painted its line past the axis
     and out over the card (Rene 2026-09-06: measurement exports are REW
     3-column "Hz dB phase" files and some sweep well beyond the audible
     band — Halcyon's runs 0.37 Hz to 20036 Hz, which is the line that
     escaped to the left of 20 Hz).

     Trim rather than drop: the boundary sample is interpolated in log-f, so
     a curve still reaches the exact left and right edges instead of
     stopping short of them. Non-numeric and non-positive frequencies go
     too — log(0) is -Infinity and would poison the whole path. */
  var F_MIN = 20, F_MAX = 20000;
  function interpLog(a, b, f) {
    var t = (Math.log(f) - Math.log(a[0])) / (Math.log(b[0]) - Math.log(a[0]));
    return isFinite(t) ? a[1] + t * (b[1] - a[1]) : a[1];
  }
  function clampCurve(pts) {
    if (!Array.isArray(pts)) return null;
    var src = pts.filter(function (d) {
      return Array.isArray(d) && isFinite(d[0]) && isFinite(d[1]) && d[0] > 0;
    }).sort(function (a, b) { return a[0] - b[0]; });
    var out = [];
    for (var i = 0; i < src.length; i++) {
      var p = src[i], prev = src[i - 1];
      if (p[0] < F_MIN) continue;
      if (prev && prev[0] < F_MIN && p[0] > F_MIN) out.push([F_MIN, interpLog(prev, p, F_MIN)]);
      if (p[0] > F_MAX) {
        if (prev && prev[0] < F_MAX) out.push([F_MAX, interpLog(prev, p, F_MAX)]);
        break;
      }
      out.push(p);
    }
    return out.length > 2 ? out : null;
  }

  /* same text parser as pk-tuning.js: "hz,db" per line, any separator,
     headers and #-comments skipped */
  function parseCurveText(raw) {
    if (typeof raw !== 'string') return null;
    var out = [];
    raw.split(/[\r\n]+/).forEach(function (line) {
      line = line.trim();
      if (!line || /^[#;*/]/.test(line)) return;
      var cols = line.split(/[\s,;\t]+/).filter(Boolean);
      if (cols.length < 2) return;
      var hz = parseFloat(cols[0]), db = parseFloat(cols[1]);
      if (isFinite(hz) && isFinite(db) && hz > 0) out.push([hz, db]);
    });
    return clampCurve(out);
  }

  var bisect = d3.bisector(function (d) { return d[0]; }).left;
  function sampleAt(pts, f) {
    if (f <= pts[0][0]) return pts[0][1];
    if (f >= pts[pts.length - 1][0]) return pts[pts.length - 1][1];
    var i = Math.min(Math.max(bisect(pts, f), 1), pts.length - 1);
    return interpLog(pts[i - 1], pts[i], f);
  }

  /* ---- state ---- */
  var baselinePts = null, baselineLabel = root.querySelector('#ms-fr-chart').getAttribute('data-baseline-label') || 'Reference';
  (function () {
    var el = document.getElementById('ms-fr-baseline');
    if (!el) return;
    var raw = el.textContent;
    try { var s = JSON.parse(raw); if (typeof s === 'string') raw = s; } catch (e) { /* plain text */ }
    baselinePts = parseCurveText(raw);
  })();

  var picked = []; /* [{handle, name, pts, color}] in pick order */
  var cache = {};  /* handle → {name, pts} */
  var normalize = true;
  var normBox = root.querySelector('[data-ms-normalize]');
  if (normBox) normalize = normBox.checked;

  function freeColor() {
    var used = picked.map(function (c) { return c.color; });
    if (baselinePts) used.push(PALETTE[0]);
    for (var i = 0; i < PALETTE.length; i++) if (used.indexOf(PALETTE[i]) === -1) return PALETTE[i];
    return PALETTE[PALETTE.length - 1];
  }
  function displayCurves() {
    var out = [];
    if (baselinePts) out.push({ name: baselineLabel, pts: baselinePts, color: PALETTE[0], baseline: true });
    picked.forEach(function (c) { out.push(c); });
    return out.map(function (c) {
      if (!normalize) return c;
      var shift = 60 - sampleAt(c.pts, 1000);
      return {
        name: c.name, color: c.color, baseline: c.baseline,
        pts: c.pts.map(function (d) { return [d[0], d[1] + shift]; })
      };
    });
  }

  var BANDS = [
    { name: 'Sub-bass',   short: 'Sub',     from: 20,    to: 60,    range: '20–60 Hz',       blurb: 'Deep rumble, physical impact, electronic bass, kick-drum extension.' },
    { name: 'Mid-bass',   short: 'Bass',    from: 60,    to: 250,   range: '60–250 Hz',      blurb: 'Bass punch, warmth, bass-guitar body, kick-drum weight.' },
    { name: 'Lower mids', short: 'Lo mids', from: 250,   to: 1000,  range: '250 Hz – 1 kHz', blurb: 'Instrument body, vocal warmth, thickness, fullness.' },
    { name: 'Upper mids', short: 'Hi mids', from: 1000,  to: 4000,  range: '1–4 kHz',        blurb: 'Vocal presence, clarity, guitars, attack, intelligibility.' },
    { name: 'Treble',     short: 'Treble',  from: 4000,  to: 10000, range: '4–10 kHz',       blurb: 'Detail, brightness, cymbal energy, consonants, sharpness.' },
    { name: 'Air',        short: 'Air',     from: 10000, to: 20000, range: '10–20 kHz',      blurb: 'Openness, shimmer, spaciousness, upper-harmonic extension.' }
  ];
  var X_TICKS = [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];
  var X_TICKS_SMALL = [20, 100, 500, 1000, 2000, 10000, 20000];
  var MAJOR = [50, 100, 200, 2000, 5000, 10000];
  function fmtHz(v, small) {
    if (small) return v >= 1000 ? (v / 1000) + 'k' : '' + v;
    return v >= 1000 ? (v / 1000) + 'k' + (v === 20000 ? 'Hz' : '') : v + (v === 20 ? 'Hz' : '');
  }

  var svg, x, y, plotW, plotH, bandAway;
  var PLOT_ASPECT = 584 / 1416;

  function yDomain(curves) {
    if (!curves.length) return [40, 81];
    var lo = Infinity, hi = -Infinity;
    curves.forEach(function (c) {
      c.pts.forEach(function (d) {
        if (d[0] < 20 || d[0] > 20000) return;
        if (d[1] < lo) lo = d[1];
        if (d[1] > hi) hi = d[1];
      });
    });
    lo = Math.floor((lo - 3) / 5) * 5;
    hi = Math.ceil((hi + 3) / 5) * 5;
    while (hi - lo < 30) { lo -= 5; hi += 5; } /* never over-zoom a flat pick */
    return [lo, hi];
  }

  function lineGen() {
    return d3.line()
      .x(function (d) { return x(d[0]); })
      .y(function (d) { return y(d[1]); })
      .curve(d3.curveCatmullRom.alpha(0.6));
  }

  function build(animateColor) {
    mount.innerHTML = '';
    var curves = displayCurves();
    var W = mount.clientWidth;
    if (!W) return;
    var isSmall = W < 720;
    /* small left gutter is 42, not pk-tuning's 34: this mount goes full-bleed
       on phones, so the dB labels' own width is all the margin they get
       (QA: "70dB" clipped to "0dB" at 34) */
    var m = { top: 10, right: isSmall ? 8 : 10, bottom: isSmall ? 26 : 34, left: isSmall ? 42 : 52 };
    plotW = W - m.left - m.right;
    plotH = Math.round(plotW * PLOT_ASPECT);
    var H = plotH + m.top + m.bottom;

    x = d3.scaleLog().domain([20, 20000]).range([0, plotW]);
    y = d3.scaleLinear().domain(yDomain(curves)).range([plotH, 0]);

    svg = d3.select(mount).append('svg')
      .attr('width', W).attr('height', H)
      .attr('viewBox', '0 0 ' + W + ' ' + H)
      .attr('role', 'img')
      .attr('aria-label', 'Frequency response comparison graph');
    var g = svg.append('g').attr('transform', 'translate(' + m.left + ',' + m.top + ')');

    var k = plotW / 1416;
    var defs = svg.append('defs');
    var field = defs.append('radialGradient').attr('id', 'msfr-field')
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('cx', plotW * 0.772).attr('cy', plotH * 0.258).attr('r', plotW * 0.74);
    FIELD_STOPS.forEach(function (st) { field.append('stop').attr('offset', st[0]).attr('stop-color', st[1]); });
    var areaFill = defs.append('linearGradient').attr('id', 'msfr-area')
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', 0).attr('y1', plotH * 0.2).attr('x2', 0).attr('y2', plotH * 1.03);
    areaFill.append('stop').attr('offset', 0).attr('stop-color', AREA_TOP);
    areaFill.append('stop').attr('offset', 1).attr('stop-color', AREA_BOTTOM).attr('stop-opacity', 0.2);
    /* hard geometric guard so nothing paints outside the field: the data is
       already trimmed to 20 Hz – 20 kHz, this catches what data cannot —
       Catmull-Rom overshoot at the two end samples and the stroke glow */
    defs.append('clipPath').attr('id', 'msfr-clip')
      .append('rect').attr('x', 0).attr('y', 0).attr('width', plotW).attr('height', plotH);
    /* one glow filter per palette color so every line carries its own halo */
    PALETTE.forEach(function (c, i) {
      defs.append('filter').attr('id', 'msfr-glow-' + i)
        .attr('x', '-5%').attr('y', '-20%').attr('width', '110%').attr('height', '140%')
        .append('feDropShadow').attr('dx', 0).attr('dy', 0)
        .attr('stdDeviation', Math.max(2.5, 6 * k))
        .attr('flood-color', c).attr('flood-opacity', 0.8);
    });

    g.append('rect').attr('x', 0).attr('y', 0).attr('width', plotW).attr('height', plotH)
      .attr('fill', 'url(#msfr-field)');

    /* area belongs to the first curve only — the visual language of the PDP graph */
    if (curves.length) {
      var area = d3.area()
        .x(function (d) { return x(d[0]); })
        .y0(function () { return plotH; })
        .y1(function (d) { return y(d[1]); })
        .curve(d3.curveCatmullRom.alpha(0.6));
      g.append('g').attr('clip-path', 'url(#msfr-clip)')
        .append('path').attr('fill', 'url(#msfr-area)').attr('opacity', 0.55).attr('stroke', 'none')
        .attr('d', area(curves[0].pts));
    }

    var minors = x.ticks(40).filter(function (v) { return v >= 20 && v <= 20000; });
    g.selectAll('.grid-v').data(minors).enter().append('line')
      .attr('x1', function (d) { return x(d); }).attr('x2', function (d) { return x(d); })
      .attr('y1', 0).attr('y2', plotH)
      .attr('stroke', function (d) { return MAJOR.indexOf(d) > -1 ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.2)'; })
      .attr('stroke-width', 1).attr('shape-rendering', 'crispEdges');
    var yTicks = y.ticks(Math.max(3, Math.round((y.domain()[1] - y.domain()[0]) / 10)))
      .filter(function (v) { return v > y.domain()[0] && v < y.domain()[1]; });
    yTicks.forEach(function (v) {
      g.append('line').attr('x1', 0).attr('x2', plotW).attr('y1', y(v)).attr('y2', y(v))
        .attr('stroke', 'rgba(0,0,0,0.2)').attr('stroke-width', 1).attr('shape-rendering', 'crispEdges');
    });
    [0, plotH].forEach(function (yy) {
      g.append('line').attr('x1', 0).attr('x2', plotW).attr('y1', yy).attr('y2', yy)
        .attr('stroke', '#000').attr('stroke-width', 1).attr('shape-rendering', 'crispEdges');
    });
    g.append('line').attr('x1', 0).attr('x2', 0).attr('y1', 0).attr('y2', plotH)
      .attr('stroke', '#D7DDF3').attr('stroke-width', 1);

    yTicks.forEach(function (v) {
      svg.append('text').attr('x', m.left - 8).attr('y', m.top + y(v) + 4)
        .attr('text-anchor', 'end').attr('font-family', FONT_SANS)
        .attr('font-size', isSmall ? 10 : 14).attr('font-weight', 600).attr('fill', '#000')
        .text(v + 'dB');
    });
    var hzLabels = (isSmall ? X_TICKS_SMALL : X_TICKS).map(function (v) {
      return {
        node: svg.append('text').attr('x', m.left + x(v)).attr('y', H - 8)
          .attr('text-anchor', v === 20 ? 'start' : v === 20000 ? 'end' : 'middle')
          .attr('font-family', FONT_SANS).attr('font-size', isSmall ? 10 : 14)
          .attr('font-weight', 600).attr('fill', '#000')
          .text(fmtHz(v, isSmall)).node()
      };
    });
    (function cullHz() {
      var PAD = isSmall ? 2 : 6;
      var boxes = hzLabels.map(function (l) {
        var b = l.node.getBBox();
        return { node: l.node, x0: b.x, x1: b.x + b.width };
      });
      if (!boxes.length) return;
      var last = boxes[boxes.length - 1];
      var prev = boxes[0];
      for (var i = 1; i < boxes.length - 1; i++) {
        var b = boxes[i];
        if (b.x0 < prev.x1 + PAD || b.x1 + PAD > last.x0) b.node.remove();
        else prev = b;
      }
    })();

    BANDS.forEach(function (b, i) {
      if (!i) return;
      g.append('line')
        .attr('x1', x(b.from)).attr('x2', x(b.from)).attr('y1', 0).attr('y2', plotH)
        .attr('stroke', 'rgba(255,255,255,0.34)').attr('stroke-width', 1)
        .attr('stroke-dasharray', '5 5').attr('shape-rendering', 'crispEdges');
    });

    /* ---- the curves ---- */
    /* gap is ~2× the dash — with the glow halo an even 1:1 pattern reads as
       a solid line at desktop widths (QA finding on the pink curve) */
    var dash = Math.max(2, 3 * k);
    var dashPattern = dash + ' ' + (dash * 2.1).toFixed(1);
    var curveLayer = g.append('g').attr('clip-path', 'url(#msfr-clip)');
    curves.forEach(function (c, i) {
      var p = curveLayer.append('path').attr('fill', 'none')
        .attr('stroke', c.color)
        .attr('stroke-width', i === 0 ? Math.max(2, 4 * k) : Math.max(1.8, 3.4 * k))
        .attr('stroke-linecap', 'round')
        .attr('filter', 'url(#msfr-glow-' + Math.max(0, PALETTE.indexOf(c.color)) + ')')
        .attr('d', lineGen()(c.pts));
      if (i > 0) p.attr('stroke-dasharray', dashPattern);
      if (!reduceMotion && animateColor && c.color === animateColor) {
        var len = p.node().getTotalLength();
        p.attr('stroke-dasharray', len).attr('stroke-dashoffset', len)
          .transition().duration(900).ease(d3.easeCubicInOut).attr('stroke-dashoffset', 0)
          .on('end', function () {
            p.attr('stroke-dashoffset', null)
              .attr('stroke-dasharray', i > 0 ? dashPattern : null);
          });
      }
    });

    /* empty state: the hint sits in the field where the curve will go */
    if (!curves.length) {
      g.append('text').attr('x', plotW / 2).attr('y', plotH / 2)
        .attr('text-anchor', 'middle').attr('font-family', FONT_SANS)
        .attr('font-size', isSmall ? 13 : 17).attr('font-weight', 600)
        .attr('fill', 'rgba(255,255,255,0.65)')
        .text(mount.getAttribute('data-empty-hint') || 'Pick a model below');
    }

    /* ---- hover crosshair: one dot + value per curve ---- */
    if (curves.length) {
      var hoverLayer = g.append('g').style('display', 'none');
      hoverLayer.append('line').attr('y1', 0).attr('y2', plotH)
        .attr('stroke', 'rgba(255,255,255,0.5)').attr('stroke-dasharray', '3,3');
      var dots = curves.map(function (c) {
        return hoverLayer.append('circle').attr('r', 4)
          .attr('fill', c.color).attr('stroke', '#0B0A1E').attr('stroke-width', 1.5);
      });
      var tip = hoverLayer.append('text').attr('font-family', FONT_MONO).attr('font-size', 12)
        .attr('fill', '#FFFFFF').attr('stroke', 'rgba(11,10,30,0.65)').attr('stroke-width', 3)
        .attr('paint-order', 'stroke').attr('stroke-linejoin', 'round');
      svg.append('rect')
        .attr('x', m.left).attr('y', m.top).attr('width', plotW).attr('height', plotH)
        .attr('fill', 'transparent')
        .on('mouseenter', function () { hoverLayer.style('display', null); })
        .on('mouseleave', function () { hoverLayer.style('display', 'none'); })
        .on('mousemove', function (ev) {
          var px = d3.pointer(ev, this)[0] - m.left;
          var f = Math.min(20000, Math.max(20, x.invert(px)));
          hoverLayer.select('line').attr('x1', px).attr('x2', px);
          var vals = curves.map(function (c, i) {
            var v = sampleAt(c.pts, f);
            dots[i].attr('cx', px).attr('cy', y(v));
            return v;
          });
          var lbl = (f >= 1000 ? (f / 1000).toFixed(1) + 'kHz' : Math.round(f) + 'Hz') + ' · ' +
            vals.map(function (v) { return v.toFixed(1); }).join(' / ') + 'dB';
          tip.attr('x', Math.min(px + 10, plotW - 40 - lbl.length * 7))
            .attr('y', Math.max(y(vals[0]) - 12, 14)).text(lbl);
        });
    }

    buildBands(m, isSmall);
  }

  /* ---- named band strip along the top (ported from pk-tuning.js) ---- */
  function buildBands(m, isSmall) {
    var stripH = isSmall ? 19 : 28;
    var layer = svg.append('g').attr('transform', 'translate(' + m.left + ',' + m.top + ')');
    var card = document.createElement('div');
    card.className = 'pk-fr-band-tip' + (isSmall ? ' is-sm' : '');
    card.hidden = true;
    mount.appendChild(card);

    var items = [], open = -1;
    function close() {
      if (open < 0) return;
      items[open].wash.attr('opacity', 0);
      items[open].label.attr('fill', 'rgba(255,255,255,0.6)');
      card.hidden = true;
      open = -1;
    }
    function show(i) {
      if (open === i) return;
      close();
      open = i;
      var it = items[i];
      it.wash.attr('opacity', 0.09);
      it.label.attr('fill', '#FFFFFF');
      card.innerHTML = '';
      var h = document.createElement('b'); h.textContent = it.band.name;
      var r = document.createElement('i'); r.textContent = it.band.range;
      var t = document.createElement('span'); t.textContent = it.band.blurb;
      card.appendChild(h); card.appendChild(r); card.appendChild(t);
      card.hidden = false;
      var half = card.offsetWidth / 2;
      var cx = m.left + (it.x0 + it.x1) / 2;
      card.style.left = Math.round(Math.min(Math.max(cx - half, 4), mount.clientWidth - card.offsetWidth - 4)) + 'px';
      card.style.top = Math.round(isSmall
        ? m.top + plotH - card.offsetHeight - 8
        : m.top + stripH + 8) + 'px';
    }
    BANDS.forEach(function (b, i) {
      var x0 = x(b.from), x1 = x(b.to), w = x1 - x0;
      var wash = layer.append('rect').attr('x', x0).attr('y', 0).attr('width', w).attr('height', plotH)
        .attr('fill', '#FFFFFF').attr('opacity', 0).style('pointer-events', 'none');
      var label = layer.append('text')
        .attr('x', x0 + w / 2).attr('y', stripH * 0.66).attr('text-anchor', 'middle')
        .attr('font-family', FONT_SANS).attr('font-size', isSmall ? 8 : 11)
        .attr('font-weight', 600).attr('letter-spacing', isSmall ? '0' : '0.08em')
        .attr('fill', 'rgba(255,255,255,0.6)').style('pointer-events', 'none')
        .text(b.name.toUpperCase());
      var room = w - (isSmall ? 4 : 10);
      if (label.node().getComputedTextLength() > room) label.text(b.short.toUpperCase());
      if (label.node().getComputedTextLength() > room) label.text('');
      var hit = layer.append('rect').attr('x', x0).attr('y', 0).attr('width', w).attr('height', stripH)
        .attr('fill', 'transparent').style('cursor', 'help')
        .attr('tabindex', 0).attr('role', 'button')
        .attr('aria-label', b.name + ', ' + b.range + '. ' + b.blurb);
      items.push({ band: b, wash: wash, label: label, x0: x0, x1: x1 });
      hit.on('pointerenter', function (ev) { if (ev.pointerType !== 'touch') show(i); })
        .on('pointerleave', function (ev) { if (ev.pointerType !== 'touch') close(); })
        .on('click', function (ev) { ev.stopPropagation(); open === i ? close() : show(i); })
        .on('focus', function () { show(i); })
        .on('blur', close)
        .on('keydown', function (ev) { if (ev.key === 'Escape') close(); });
    });
    if (bandAway) document.removeEventListener('click', bandAway);
    bandAway = close;
    document.addEventListener('click', bandAway);
  }

  /* ================================ picker ================================ */
  var tiles = [].slice.call(root.querySelectorAll('[data-ms-tile]'));
  var chipList = root.querySelector('[data-ms-selected]');
  var countEl = root.querySelector('[data-ms-count]');
  var clearBtn = root.querySelector('[data-ms-clear]');
  var searchIn = root.querySelector('[data-ms-search]');
  var noResults = root.querySelector('[data-ms-noresults]');

  function note(msg) {
    if (!noResults) return;
    noResults.textContent = msg;
    noResults.classList.remove('hidden');
  }
  function clearNote() {
    if (noResults) noResults.classList.add('hidden');
  }

  function syncUrl() {
    if (!window.history || !history.replaceState) return;
    try {
      var url = new URL(location.href);
      if (picked.length) url.searchParams.set('iems', picked.map(function (c) { return c.handle; }).join(','));
      else url.searchParams.delete('iems');
      history.replaceState(null, '', url);
    } catch (e) { /* URL API missing */ }
  }

  function sync(animateColor) {
    if (countEl) countEl.textContent = picked.length + ' of ' + MAXN;
    if (clearBtn) clearBtn.hidden = picked.length < 2;
    tiles.forEach(function (t) {
      var on = picked.some(function (c) { return c.handle === t.getAttribute('data-ms-tile'); });
      t.setAttribute('aria-pressed', String(on));
      t.disabled = !on && picked.length >= MAXN;
    });
    if (chipList) {
      chipList.innerHTML = '';
      /* the baseline gets a legend chip too — an anonymous curve on a
         comparison graph is a defect, not a mystery (QA finding). Not a
         button: it cannot be removed, only replaced in the customizer. */
      if (baselinePts) {
        var bl = document.createElement('li');
        bl.className = 'cmp-selchip';
        bl.style.cursor = 'default';
        var bdot = document.createElement('i');
        bdot.style.cssText = 'display:inline-block;width:11px;height:11px;border-radius:99px;flex:none;background:' + PALETTE[0] + ';box-shadow:inset 0 0 0 1px rgba(0,0,0,0.22)';
        bl.appendChild(bdot);
        bl.appendChild(document.createTextNode(' ' + baselineLabel));
        chipList.appendChild(bl);
      }
      picked.forEach(function (c) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'cmp-selchip';
        b.setAttribute('data-ms-unpick', c.handle);
        b.setAttribute('aria-label', 'Remove ' + c.name + ' from the graph');
        var dot = document.createElement('i');
        dot.style.cssText = 'display:inline-block;width:11px;height:11px;border-radius:99px;flex:none;background:' + c.color + ';box-shadow:inset 0 0 0 1px rgba(0,0,0,0.22)';
        b.appendChild(dot);
        b.appendChild(document.createTextNode(' ' + c.name + ' '));
        var xm = document.createElement('span');
        xm.textContent = '×';
        b.appendChild(xm);
        var li = document.createElement('li');
        li.appendChild(b);
        chipList.appendChild(li);
      });
    }
    syncUrl();
    build(animateColor);
  }

  function add(handle, tileName) {
    if (picked.length >= MAXN) return;
    if (picked.some(function (c) { return c.handle === handle; })) return;
    if (cache[handle]) {
      var color = freeColor();
      picked.push({ handle: handle, name: cache[handle].name, pts: cache[handle].pts, color: color });
      clearNote();
      sync(color);
      return;
    }
    fetch('/products/' + handle + '?view=frdata')
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var pts = (d && Array.isArray(d.fr) && d.fr.length > 2)
          ? clampCurve(d.fr)
          : parseCurveText(d && d.text);
        if (!pts) { note('No measurement data for “' + (tileName || handle) + '” yet.'); sync(); return; }
        cache[handle] = { name: (d && d.title) || tileName || handle, pts: pts };
        add(handle, tileName);
      })
      .catch(function () { note('Could not load measurement data.'); sync(); });
  }
  function remove(handle) {
    picked = picked.filter(function (c) { return c.handle !== handle; });
    sync();
  }

  root.addEventListener('click', function (e) {
    var t = e.target.closest('[data-ms-tile]');
    if (t && !t.disabled) {
      var h = t.getAttribute('data-ms-tile');
      if (picked.some(function (c) { return c.handle === h; })) remove(h);
      else add(h, t.getAttribute('data-name'));
      return;
    }
    var u = e.target.closest('[data-ms-unpick]');
    if (u) { remove(u.getAttribute('data-ms-unpick')); return; }
    if (clearBtn && e.target === clearBtn) { picked = []; sync(); }
  });

  if (searchIn) {
    searchIn.addEventListener('input', function () {
      var q = searchIn.value.trim().toLowerCase();
      var hits = 0;
      tiles.forEach(function (t) {
        var hit = !q || (t.getAttribute('data-search') || '').indexOf(q) > -1;
        t.hidden = !hit;
        if (hit) hits++;
      });
      if (noResults) {
        noResults.textContent = noResults.getAttribute('data-default') || noResults.textContent;
        noResults.classList.toggle('hidden', hits > 0);
      }
    });
    if (noResults) noResults.setAttribute('data-default', noResults.textContent);
  }

  if (normBox) {
    normBox.addEventListener('change', function () {
      normalize = normBox.checked;
      build();
    });
  }

  /* deep link: ?iems=handle,handle */
  (function () {
    try {
      var q = new URL(location.href).searchParams.get('iems');
      if (!q) return;
      q.split(',').slice(0, MAXN).forEach(function (h) {
        h = h.trim();
        var tile = root.querySelector('[data-ms-tile="' + (window.CSS && CSS.escape ? CSS.escape(h) : h) + '"]');
        if (tile) add(h, tile.getAttribute('data-name'));
      });
    } catch (e) { /* URL API missing */ }
  })();

  sync();
  var t;
  if ('ResizeObserver' in window) new ResizeObserver(function () { clearTimeout(t); t = setTimeout(function () { build(); }, 150); }).observe(mount);
  else window.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(function () { build(); }, 150); });
})();
